import { useCallback } from "react";
import { batch, useDispatch } from "react-redux";
import { addresses } from "src/configs/constants";
import { clearPendingTxn, fetchPendingTxns } from "src/slices/PendingTxnsSlice";
import { useWeb3Context } from ".";
import { ethers } from "ethers";
import { abi as InfoHelperABI } from "../abis/InfoHelper.json";
import { abi as ShareholdersRebateABI } from "../abis/ShareholdersRebate.json";
import { abi as BurnToScoreABI } from "../abis/BurnToScore.json";
import { abi as DIDABI } from "../abis/DID.json";
import { loadAppDetails } from "../slices/AppSlice";
import { toastSuccess } from "src/helpers/toastHelpers";
import { handlerError } from "src/helpers/errorHelpers";
import { useUpDataBlockNumber } from "src/lib/hooks/useUpDataBlockNumber";
import { useEthersSigner } from "./web3Context";
import { useDIDHelperContract, useTokenVestingContract } from "./useContract";

export default function usePid() {
  const dispatch = useDispatch();
  const { chainID, address, provider } = useWeb3Context();
  const signer = useEthersSigner();

  const updateBlockNumber = useUpDataBlockNumber();

  const DIDHelperContract = useDIDHelperContract();
  const TokenVestingContract = useTokenVestingContract();

  const doBoost = useCallback(
    async (amount) => {
      if (chainID && provider && address && amount) {
        let tx: any;
        try {
          const BurnToScoreContract = new ethers.Contract(
            addresses[chainID].BurnToScore,
            BurnToScoreABI,
            signer
          );
          tx = await BurnToScoreContract.burnToken(amount);
          dispatch(
            fetchPendingTxns({ txnHash: tx.hash, text: "boost", type: "boost" })
          );
          const data = await tx.wait();
          updateBlockNumber(data);
          return tx;
        } catch (e: unknown) {
          handlerError(e);
        } finally {
          if (tx) {
            batch(() => {
              dispatch(
                loadAppDetails({ networkID: chainID, provider, address })
              );
              dispatch(clearPendingTxn(tx.hash));
              toastSuccess("Boost Success!", tx.hash);
            });
          }
        }
      }
    },
    [chainID, provider, address, signer, dispatch, updateBlockNumber]
  );

  const doAccelerate = useCallback(async () => {
    if (chainID && provider && address && TokenVestingContract) {
      let tx: any;
      try {
        tx = await TokenVestingContract.quit();
        dispatch(
          fetchPendingTxns({
            txnHash: tx.hash,
            text: "accelerate",
            type: "accelerate",
          })
        );
        const data = await tx.wait();
        updateBlockNumber(data);
        return tx;
      } catch (e: unknown) {
        handlerError(e);
      } finally {
        if (tx) {
          batch(() => {
            dispatch(loadAppDetails({ networkID: chainID, provider, address }));
            dispatch(clearPendingTxn(tx.hash));
            toastSuccess("Accelerate Success!", tx.hash);
          });
        }
      }
    }
  }, [
    chainID,
    provider,
    address,
    TokenVestingContract,
    dispatch,
    updateBlockNumber,
  ]);

  const doStopVesting = useCallback(async () => {
    if (chainID && provider && address && TokenVestingContract) {
      let tx: any;
      try {
        tx = await TokenVestingContract.stopVesting();
        dispatch(
          fetchPendingTxns({
            txnHash: tx.hash,
            text: "stop",
            type: "stop",
          })
        );
        const data = await tx.wait();
        updateBlockNumber(data);
        return tx;
      } catch (e: unknown) {
        handlerError(e);
      } finally {
        if (tx) {
          batch(() => {
            dispatch(loadAppDetails({ networkID: chainID, provider, address }));
            dispatch(clearPendingTxn(tx.hash));
            toastSuccess("Stop Success!", tx.hash);
          });
        }
      }
    }
  }, [
    chainID,
    provider,
    address,
    TokenVestingContract,
    dispatch,
    updateBlockNumber,
  ]);

  const doClaimRebates = useCallback(async () => {
    if (chainID && provider && address) {
      let tx: any;
      try {
        tx = await DIDHelperContract.claimRebates(address);
        dispatch(
          fetchPendingTxns({
            txnHash: tx.hash,
            text: "claim_rebates",
            type: "claim_rebates",
          })
        );
        const data = await tx.wait();
        updateBlockNumber(data);
        return tx;
      } catch (e: unknown) {
        handlerError(e);
      } finally {
        if (tx) {
          batch(() => {
            dispatch(loadAppDetails({ networkID: chainID, provider, address }));
            dispatch(clearPendingTxn(tx.hash));
            toastSuccess("Claim Success!", tx.hash);
          });
        }
      }
    }
  }, [
    chainID,
    provider,
    address,
    DIDHelperContract,
    dispatch,
    updateBlockNumber,
  ]);

  const changeName = useCallback(
    async (name) => {
      if (chainID && provider && address && name) {
        let tx: any;
        try {
          const DIDContract = new ethers.Contract(
            addresses[chainID].DID,
            DIDABI,
            signer
          );
          tx = await DIDContract.setNickName(name);
          dispatch(
            fetchPendingTxns({ txnHash: tx.hash, text: "edit", type: "edit" })
          );
          const data = await tx.wait();
          updateBlockNumber(data);
          return tx;
        } catch (e: unknown) {
          handlerError(e);
        } finally {
          if (tx) {
            batch(() => {
              dispatch(
                loadAppDetails({ networkID: chainID, provider, address })
              );
              dispatch(clearPendingTxn(tx.hash));
              toastSuccess("Edit Success!", tx.hash);
            });
          }
        }
      }
    },
    [chainID, provider, address, signer, dispatch, updateBlockNumber]
  );

  const mint = useCallback(
    async (inviteCode) => {
      if (chainID && provider && address && inviteCode) {
        let tx: any;
        try {
          const DIDContract = new ethers.Contract(
            addresses[chainID].DID,
            DIDABI,
            signer
          );
          tx = await DIDContract.safeMint(inviteCode);
          dispatch(
            fetchPendingTxns({ txnHash: tx.hash, text: "mint", type: "mint" })
          );
          const data = await tx.wait();
          updateBlockNumber(data);
          return tx;
        } catch (e: unknown) {
          handlerError(e);
        } finally {
          if (tx) {
            batch(() => {
              dispatch(
                loadAppDetails({ networkID: chainID, provider, address })
              );
              dispatch(clearPendingTxn(tx.hash));
              toastSuccess("Mint Success!", tx.hash);
            });
          }
        }
      }
    },
    [chainID, provider, address, signer, dispatch, updateBlockNumber]
  );

  const mintWithName = useCallback(
    async (inviteCode, name) => {
      if (chainID && provider && address && name) {
        let tx: any;
        try {
          const DIDContract = new ethers.Contract(
            addresses[chainID].DID,
            DIDABI,
            signer
          );
          const estimation = await DIDContract.estimateGas.mintWithName(
            inviteCode,
            name
          );
          tx = await DIDContract.mintWithName(inviteCode, name, {
            gasLimit: estimation.mul(150).div(100),
          });
          dispatch(
            fetchPendingTxns({ txnHash: tx.hash, text: "mint", type: "mint" })
          );
          const data = await tx.wait();
          updateBlockNumber(data);
          return tx;
        } catch (e: unknown) {
          console.log("mint error", e);
          handlerError(e);
        } finally {
          if (tx) {
            batch(() => {
              dispatch(
                loadAppDetails({ networkID: chainID, provider, address })
              );
              dispatch(clearPendingTxn(tx.hash));
              toastSuccess("Mint Success!", tx.hash);
            });
          }
        }
      }
    },
    [chainID, provider, address, signer, dispatch, updateBlockNumber]
  );

  const claimRewards = useCallback(
    async (round) => {
      if (chainID && provider && address) {
        let tx: any;
        try {
          const ShareholdersRebateContract = new ethers.Contract(
            addresses[chainID].ShareholdersRebate,
            ShareholdersRebateABI,
            signer
          );
          tx = await ShareholdersRebateContract.claim(round);
          dispatch(
            fetchPendingTxns({ txnHash: tx.hash, text: "claim", type: "claim" })
          );
          const data = await tx.wait();
          updateBlockNumber(data);
          return tx;
        } catch (e: unknown) {
          handlerError(e);
        } finally {
          if (tx) {
            batch(() => {
              dispatch(
                loadAppDetails({ networkID: chainID, provider, address })
              );
              dispatch(clearPendingTxn(tx.hash));
              toastSuccess("Claim Success!", tx.hash);
            });
          }
        }
      }
    },
    [chainID, provider, address, signer, dispatch, updateBlockNumber]
  );

  const refreshCompleteness = useCallback(async () => {
    if (chainID && provider && address) {
      let tx: any;
      try {
        const InfoHelperContract = new ethers.Contract(
          addresses[chainID].InfoHelper,
          InfoHelperABI,
          signer
        );
        const needUpdate = Number(
          await InfoHelperContract.needUpdate(
            addresses[chainID].ShareholdersRebate,
            address
          )
        );
        const isNeedUpdate = needUpdate > 0 ? true : false;
        // console.log("isNeedUpdate", isNeedUpdate, needUpdate);
        const ShareholdersRebateContract = new ethers.Contract(
          addresses[chainID].ShareholdersRebate,
          ShareholdersRebateABI,
          signer
        );
        if (isNeedUpdate) {
          if (needUpdate == 1) {
            tx = await ShareholdersRebateContract.updateCompleteness(
              address,
              0
            );
          }
          if (needUpdate == 2) {
            tx = await ShareholdersRebateContract.updateCompleteness(
              address,
              1
            );
          }

          dispatch(
            fetchPendingTxns({
              txnHash: tx.hash,
              text: "refresh",
              type: "refresh",
            })
          );
          const data = await tx.wait();
          updateBlockNumber(data);
          return tx;
        }
      } catch (e: unknown) {
        handlerError(e);
      } finally {
        if (tx) {
          batch(() => {
            dispatch(loadAppDetails({ networkID: chainID, provider, address }));
            dispatch(clearPendingTxn(tx.hash));
            toastSuccess("refresh Success!", tx.hash);
          });
        }
      }
    }
  }, [chainID, provider, address, signer, dispatch, updateBlockNumber]);

  return {
    claimRewards,
    mint,
    changeName,
    refreshCompleteness,
    mintWithName,
    doBoost,
    doAccelerate,
    doStopVesting,
    doClaimRebates,
  };
}
